import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type { EconomicCalendarEvent } from "../../hooks/useEconomicCalendar";

dayjs.extend(utc);

// The dashboard calendar's editorial selection as pure functions, so the
// slot arithmetic that decides what renders (and what the "+N more" row
// claims) is unit-testable. The rules, same as the Sectors tape: US and UK
// only, high impact when there is any, medium as the fallback, low never —
// and TIER-PURE: the lead day's display tier is the only tier that exists
// for the panel and its counts.

const COUNTRIES = new Set(["US", "GB", "UK"]);

// The panel is a rail of exactly this many fixed-height slots, so it is the
// same shape every day whatever the week holds: a busy day fills it and
// hands the rest to the door in the last slot; a quiet day pulls the coming
// days' events forward into the free slots, each carrying its date on the
// card rather than under a label of its own. Four slots end on the markets
// grid's line beside it.
export const PANEL_SLOTS = 4;
export const PANEL_ROW_HEIGHT = 56;
export const PANEL_ROW_GAP = 8;

export const compareEconomicEvents = (
  a: EconomicCalendarEvent,
  b: EconomicCalendarEvent,
): number => {
  const byTime = a.date.localeCompare(b.date);
  if (byTime !== 0) return byTime;
  const byCountry = a.country.localeCompare(b.country);
  if (byCountry !== 0) return byCountry;
  return a.event.localeCompare(b.event);
};

// What the panel shows for a given day: the countries that matter, high
// impact when there is any, medium as the fallback.
export const eventsForDay = (
  events: EconomicCalendarEvent[] | undefined,
): EconomicCalendarEvent[] => {
  const relevant = (events ?? []).filter((event) =>
    COUNTRIES.has(event.country),
  );
  const high = relevant.filter((event) => event.impact === "High");
  const shown =
    high.length > 0
      ? high
      : relevant.filter((event) => event.impact === "Medium");
  return [...shown].sort(compareEconomicEvents);
};

// ---------------------------------------------------------------------------
// The full calendar: one day at a time, every tier reachable.

export type ImpactTier = "High" | "Medium" | "Low";
export const IMPACT_TIERS: readonly ImpactTier[] = ["High", "Medium", "Low"];

export const IMPACT_COLORS: Record<ImpactTier, string> = {
  High: "#ef4444",
  Medium: "#f59e0b",
  Low: "#9ca3af",
};

export type TierCounts = Record<ImpactTier, number>;

export const tierCounts = (
  events: EconomicCalendarEvent[] | undefined,
): TierCounts => {
  const counts: TierCounts = { High: 0, Medium: 0, Low: 0 };
  for (const event of events ?? []) {
    if (!COUNTRIES.has(event.country)) continue;
    if (
      event.impact === "High" ||
      event.impact === "Medium" ||
      event.impact === "Low"
    ) {
      counts[event.impact] += 1;
    }
  }
  return counts;
};

// The tiers a day actually shows. High is always on; Medium and Low are the
// user's picks and travel with them from day to day. A day where the picked
// tiers show nothing escalates one tier at a time — high, then medium, then
// low — until something is on screen, so a quiet Wednesday never opens on an
// empty list when there are mediums to read. Escalation is per day and
// never sticks: it does not touch the user's picks.
export const resolveTiers = (
  picked: ReadonlySet<ImpactTier>,
  counts: TierCounts,
): Set<ImpactTier> => {
  const tiers = new Set<ImpactTier>(Array.from(picked));
  tiers.add("High");
  const showsSomething = () =>
    IMPACT_TIERS.some((tier) => tiers.has(tier) && counts[tier] > 0);
  for (const tier of IMPACT_TIERS) {
    if (showsSomething()) break;
    tiers.add(tier);
  }
  return tiers;
};

// One day of the full calendar, in the order it will happen: the tiers that
// are on, US and UK, chronological. (The panel's tier-purity does not apply
// here — this is the one surface where every tier is reachable.)
export const eventsForTiers = (
  events: EconomicCalendarEvent[] | undefined,
  tiers: ReadonlySet<ImpactTier>,
): EconomicCalendarEvent[] =>
  (events ?? [])
    .filter(
      (event) =>
        COUNTRIES.has(event.country) && tiers.has(event.impact as ImpactTier),
    )
    .sort(compareEconomicEvents);

const isWeekday = (date: string): boolean => {
  const day = dayjs.utc(date).day();
  return day !== 0 && day !== 6;
};

// The full calendar reads the served range one WEEK at a time: Monday to
// Friday always, Saturday and Sunday only when they actually carry an event
// (a weekend is noise until it isn't). Past days of the current week stay —
// their actuals are the week's story so far. A week that has nothing at all
// in the range still appears, so paging never skips a week. Bounded, so a
// malformed range can never spin.
const MAX_WEEKS = 8;

export const calendarWeeks = (
  range: { from: string; to: string },
  grouped: Record<string, EconomicCalendarEvent[]> | undefined,
): string[][] => {
  const from = dayjs.utc(range.from);
  const to = dayjs.utc(range.to);
  if (!from.isValid() || !to.isValid() || from.isAfter(to)) return [];

  const weeks: string[][] = [];
  // dayjs weeks start on Sunday; the market's start on Monday.
  let weekStart =
    from.day() === 0
      ? from.subtract(6, "day")
      : from.startOf("week").add(1, "day");
  while (!weekStart.isAfter(to) && weeks.length < MAX_WEEKS) {
    const days: string[] = [];
    for (let offset = 0; offset < 7; offset++) {
      const day = weekStart.add(offset, "day");
      if (day.isBefore(from) || day.isAfter(to)) continue;
      const date = day.format("YYYY-MM-DD");
      if (isWeekday(date) || (grouped?.[date]?.length ?? 0) > 0)
        days.push(date);
    }
    if (days.length > 0) weeks.push(days);
    weekStart = weekStart.add(7, "day");
  }
  return weeks;
};

// Where a week opens when paged to: today if the week holds it, else its
// first day — the one rule that returns you to today from next week and
// lands you on Monday going forward.
export const weekAnchor = (week: string[], today: string): string =>
  week.includes(today) ? today : week[0];

// A day split into its tiers, each in the order it happens — the full
// calendar's sections. Highs first because they are the day; the order is
// the hierarchy.
export const eventsByTier = (
  events: EconomicCalendarEvent[] | undefined,
): Record<ImpactTier, EconomicCalendarEvent[]> => ({
  High: eventsForTiers(events, new Set<ImpactTier>(["High"])),
  Medium: eventsForTiers(events, new Set<ImpactTier>(["Medium"])),
  Low: eventsForTiers(events, new Set<ImpactTier>(["Low"])),
});

// ---------------------------------------------------------------------------
// The panel's rail.

export interface CalendarDayGroup {
  date: string;
  events: EconomicCalendarEvent[];
}

// The day the panel leads with: today when it has anything worth showing,
// otherwise the next day that does. Both layouts start here — they differ
// only in what they do with the days AFTER it.
export const resolveLeadDay = (
  grouped: Record<string, EconomicCalendarEvent[]> | undefined,
  today: string,
): CalendarDayGroup | null => {
  const upcomingDays = Object.keys(grouped ?? {})
    .filter((day) => day >= today)
    .sort();
  for (const day of upcomingDays) {
    const events = eventsForDay(grouped?.[day]);
    if (events.length > 0) return { date: day, events };
  }
  return null;
};

// Fill the rail. TIER-PURE: the lead day's tier (high, else medium) is the
// only tier that exists for the panel and its counts — one high event never
// advertises the mediums beneath it. A lead day that overflows the rail
// takes every slot but the last, which the "+N more" door occupies; a lead
// day that fits leaves its spare slots to the days after it, in order. The
// door and a pulled-forward day can therefore never appear together.
export const buildCalendarGroups = (
  grouped: Record<string, EconomicCalendarEvent[]> | undefined,
  today: string,
): CalendarDayGroup[] => {
  const lead = resolveLeadDay(grouped, today);
  if (!lead) return [];

  const needsDoor = lead.events.length > PANEL_SLOTS;
  const leadCap = needsDoor ? PANEL_SLOTS - 1 : PANEL_SLOTS;
  const result: CalendarDayGroup[] = [
    { date: lead.date, events: lead.events.slice(0, leadCap) },
  ];
  if (needsDoor) return result;

  let filled = lead.events.length;
  const laterDays = Object.keys(grouped ?? {})
    .filter((day) => day > lead.date)
    .sort();
  for (const day of laterDays) {
    const budget = PANEL_SLOTS - filled;
    if (budget <= 0) break;
    const dayEvents = eventsForDay(grouped?.[day]);
    if (dayEvents.length === 0) continue;
    result.push({ date: day, events: dayEvents.slice(0, budget) });
    filled += Math.min(dayEvents.length, budget);
  }
  return result;
};

// Phones and the native app get THREE rows of the lead day and nothing else.
// Pull-forward exists on desktop because the calendar has a fixed height to
// fill beside the markets grid; a stacked phone column has no such height,
// so a look-ahead there is just scrolling between the user and their
// watchlist. Everything past the cap — including the rest of today — lives
// behind the "+N more" door, exactly as the overflow already does.
export const CONDENSED_MAX_ROWS = 3;
// The condensed row's outer height (padding, two lines, hairline), for
// skeletons that reserve the cut's height before it mounts.
export const CONDENSED_ROW_HEIGHT = 48;

export const buildCondensedGroups = (
  grouped: Record<string, EconomicCalendarEvent[]> | undefined,
  today: string,
): CalendarDayGroup[] => {
  const lead = resolveLeadDay(grouped, today);
  if (!lead) return [];
  return [
    { date: lead.date, events: lead.events.slice(0, CONDENSED_MAX_ROWS) },
  ];
};
